import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import DesktopNav from './navigation/DesktopNav';
import MobileNav from './navigation/MobileNav';
import { NAVIGATION_ITEMS, MASTER_LOGO_URL, EPM_MONOGRAM_URL } from './navigation/navigationConfig';
import {
  Phone,
  Menu,
  LogIn,
  LogOut,
  User,
  ArrowRight
} from 'lucide-react';

/**
 * Main header component with navigation.
 * Supports 'default' and 'dashboard' variants.
 * Desktop: White background with navy text (business-card inspired)
 * Mobile: Navy background for contrast
 */
const Header = ({ variant = 'default', companyPhone = '07300 405045' }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const { user, login, logout } = useAuth();

  // Dashboard variant - simpler header for internal pages
  if (variant === 'dashboard') {
    return (
      <header className="bg-brand-navy border-b border-brand-navy/50 sticky top-0 z-40 shadow-sm" data-testid="header-dashboard">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <img src={EPM_MONOGRAM_URL} alt="EPM" className="h-10 w-auto" />
              <span className="text-white font-semibold hidden sm:block">Expert Property Maintenance</span>
            </Link>
            
            <div className="flex items-center gap-4">
              {user && (
                <>
                  <div className="hidden sm:flex items-center gap-2 text-white/80">
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <User className="w-5 h-5" />
                    )}
                    <span className="text-sm">{user.name || user.email}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={logout}
                    className="text-white border-white/30 hover:bg-white/10"
                    data-testid="logout-btn"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>
    );
  }

  // Default variant - full navigation header
  // Mobile: White background for logo visibility | Desktop: White background
  return (
    <>
      {/* Mobile Header (White background for logo visibility) */}
      <header className="lg:hidden bg-white sticky top-0 z-40 shadow-md border-b border-slate-100" data-testid="header-mobile">
        <div className="px-4 py-2">
          <div className="flex items-center justify-between gap-2">
            <Link to="/" className="flex-shrink-0" aria-label="Expert Property Maintenance home">
              <img
                src={MASTER_LOGO_URL}
                alt="Expert Property Maintenance"
                className="h-11 sm:h-12 w-auto max-w-[220px]"
              />
            </Link>

            <div className="flex items-center gap-2">
              <a
                href={`tel:${companyPhone}`}
                className="flex items-center gap-1.5 text-brand-navy hover:text-brand-yellow transition-colors"
                data-testid="phone-link-mobile"
              >
                <Phone className="w-4 h-4 text-brand-yellow" />
                <span className="font-bold text-sm hidden xs:inline">{companyPhone}</span>
              </a>

              <button
                onClick={() => setMobileMenuOpen(true)}
                className="p-2 text-brand-navy hover:bg-slate-100 rounded-lg transition-colors"
                aria-label="Open menu"
                data-testid="mobile-menu-btn"
              >
                <Menu className="w-6 h-6" />
              </button>
            </div>
          </div>

        </div>
      </header>

      {/* Desktop Header (White) - two-row layout to give branding prominence */}
      <header className="hidden lg:block bg-white sticky top-0 z-40 shadow-md border-b border-slate-100" data-testid="header-desktop">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-6 xl:px-8">
          {/* Row 1: Branding + primary actions */}
          <div className="flex items-center justify-between gap-6 pt-3 pb-2">
            {/* Logo + tagline */}
            <Link to="/" className="flex-shrink-0" aria-label="Expert Property Maintenance home">
              <img
                src={MASTER_LOGO_URL}
                alt="Expert Property Maintenance"
                className="h-[70px] xl:h-[80px] w-auto"
              />
            </Link>

            {/* Right Side Actions */}
            <div className="flex items-center gap-3 xl:gap-4">
              {/* Phone - Single line, prominent */}
              <a
                href={`tel:${companyPhone}`}
                className="flex items-center gap-2 text-brand-navy hover:text-brand-yellow transition-colors whitespace-nowrap"
                data-testid="phone-link-desktop"
              >
                <Phone className="w-4 h-4 text-brand-yellow" />
                <span className="font-bold text-sm xl:text-base">{companyPhone}</span>
              </a>

              {/* Request a Quote CTA - Primary action */}
              <Button
                size="default"
                className="bg-brand-yellow hover:bg-yellow-400 text-brand-navy font-bold px-4 xl:px-5 h-10 whitespace-nowrap text-sm xl:text-base"
                asChild
                data-testid="header-quote-btn"
              >
                <a href="/#contact">
                  Request a Quote
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </a>
              </Button>

              {/* Member Login - Understated */}
              {user ? (
                <div className="flex items-center gap-2 border-l border-slate-200 pl-3">
                  <Link
                    to="/dashboard"
                    className="flex items-center gap-2 text-brand-slate hover:text-brand-navy transition-colors"
                  >
                    {user.picture ? (
                      <img src={user.picture} alt="" className="w-7 h-7 rounded-full" />
                    ) : (
                      <User className="w-4 h-4" />
                    )}
                    <span className="text-sm font-medium hidden xl:inline">{user.name?.split(' ')[0]}</span>
                  </Link>
                  <button
                    onClick={logout}
                    className="p-1.5 text-brand-slate hover:text-brand-navy transition-colors"
                    data-testid="logout-btn"
                    title="Sign out"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={login}
                  className="flex items-center gap-1.5 text-brand-slate hover:text-brand-navy text-sm font-medium transition-colors border-l border-slate-200 pl-3"
                  data-testid="member-login-btn"
                >
                  <LogIn className="w-4 h-4" />
                  <span className="hidden xl:inline">Member</span>
                </button>
              )}
            </div>
          </div>

          {/* Row 2: Navigation */}
          <div className="border-t border-slate-100">
            <DesktopNav
              navigation={NAVIGATION_ITEMS}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />
          </div>
        </div>
      </header>

      {/* Mobile Navigation */}
      <MobileNav
        navigation={NAVIGATION_ITEMS}
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        companyPhone={companyPhone}
      />
    </>
  );
};

export default Header;
